import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AllOpenOrdersPage.css";

const LIMIT = 50;

const FuturesTradeHistoryPage = () => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);

  const [pairId, setPairId] = useState("");
  const [symbol, setSymbol] = useState("");
  const [side, setSide] = useState("");
  const [orderId, setOrderId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 1000);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [pairId, symbol, side, orderId, from, to, debouncedUser]);

  const buildParams = useCallback(() => {
    const params = { page, limit: LIMIT };
    if (pairId.trim()) params.pair_id = pairId.trim();
    if (symbol.trim()) params.symbol = symbol.trim();
    if (side) params.side = side;
    if (orderId.trim()) params.order_id = orderId.trim();
    if (from) {
      const d = new Date(from);
      if (!Number.isNaN(d.getTime())) params.from = d.toISOString();
    }
    if (to) {
      const d = new Date(to);
      if (!Number.isNaN(d.getTime())) params.to = d.toISOString();
    }
    if (debouncedUser) params.user = debouncedUser;
    return params;
  }, [page, pairId, symbol, side, orderId, from, to, debouncedUser]);

  useEffect(() => {
    const fetchTrades = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await AdminService.getGlobalFuturesTrades(buildParams());
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : null;
        if (list) {
          setTrades(list);
          setTotalPages(res?.pagination?.total_pages || 1);
          setTotalCount(
            typeof res?.pagination?.total === "number" ? res.pagination.total : null
          );
        } else {
          setTrades([]);
          setTotalPages(1);
          setTotalCount(null);
          setError(res?.message || "Failed to load futures trades.");
        }
      } catch (e) {
        console.error(e);
        setTrades([]);
        setTotalCount(null);
        setError("Failed to load futures trades.");
      } finally {
        setLoading(false);
      }
    };
    fetchTrades();
  }, [buildParams]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const formatNum = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 10 })
      : "—";

  const pairLabel = (row) =>
    row.pair?.name || row.pair?.short_name || row.symbol || "—";

  const userFullName = (u) => {
    if (!u) return "—";
    const first = u.first_name ?? u.firstName;
    const last = u.last_name ?? u.lastName;
    const parts = [first, last].filter(Boolean);
    return parts.length ? parts.join(" ") : "—";
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Futures Trading</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Futures Trade History</span>
          </p>
          <h1 className="dashboard-hero__title">Futures Trade History</h1>
          <p className="dashboard-hero__subtitle">
            Global futures fills (date range, symbol, order, user)
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-chart-line" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-history" />
              </span>
              <h2 className="openorders-section__title">Futures Trades</h2>
            </div>
            <div className="openorders-section__stats">
              <span className="openorders-stat">
                <strong>{trades.length}</strong> on page
                {totalCount != null && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
            </div>
          </div>

          {error && (
            <p style={{ color: "#dc2626", padding: "0 0.5rem 0.75rem", margin: 0 }}>{error}</p>
          )}

          <div
            className="openorders-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="text"
              placeholder="User (email / UUID / phone) — 1s debounce"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 200, flex: "1 1 200px" }}
            />
            <input
              type="text"
              placeholder="Symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 140px" }}
            />
            <input
              type="text"
              placeholder="Pair ID (ObjectId)"
              value={pairId}
              onChange={(e) => setPairId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 200px" }}
            />
            <input
              type="text"
              placeholder="Order ID (ObjectId or partial)"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 200px" }}
            />
            <select
              value={side}
              onChange={(e) => setSide(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 140px" }}
            >
              <option value="">Side (any)</option>
              <option value="LONG">LONG</option>
              <option value="SHORT">SHORT</option>
            </select>
            <label style={{ display: "flex", flexDirection: "column", fontSize: 12, gap: 2 }}>
              From
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="openorders-search-input"
                style={{ minWidth: 180, margin: 0 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", fontSize: 12, gap: 2 }}>
              To
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="openorders-search-input"
                style={{ minWidth: 180, margin: 0 }}
              />
            </label>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : trades.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table">
                  <thead>
                    <tr>
                      <th>Trade ID</th>
                      <th>Order ID</th>
                      <th>UUID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Pair</th>
                      <th>Side</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Fee</th>
                      <th>PnL</th>
                      <th>Role</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trades.map((row) => {
                      const docId = row.id ?? row._id ?? row.trade_id;
                      const orderRef =
                        row.order_id ?? row.orderId ?? row.futures_order_id;
                      const uid = row.user?.id || row.user_id;
                      const u = row.user;
                      const uuidStr = u?.uuid ?? "—";
                      const nameStr = userFullName(u);
                      const emailStr = u?.email ?? "—";
                      const phoneStr = u?.phone ?? "—";
                      const p = pairLabel(row);
                      const qty = row.quantity ?? row.qty ?? row.size;
                      const fee = row.fee ?? row.feePaid ?? row.fee_amount;
                      const marginAsset = row.pair?.margin_asset;
                      const pnlVal = row.pnl ?? row.realized_pnl ?? row.realizedPnl;
                      const roleVal =
                        row.role ?? row.liquidity ?? row.liquidity_role;
                      const updatedAt = row.updatedAt ?? row.updated_at;
                      return (
                        <tr key={docId}>
                          <td>
                            <span
                              className="openorders-order-id"
                              title={docId ? `Trade id: ${docId}` : undefined}
                            >
                              {docId ?? "—"}
                            </span>
                          </td>
                          <td>
                            <span
                              className="openorders-order-id"
                              title={orderRef ? `Futures order: ${orderRef}` : undefined}
                            >
                              {orderRef ?? "—"}
                            </span>
                          </td>
                          <td>
                            {uid ? (
                              <Link
                                to={`/dashboard/trader/${uid}`}
                                state={{ trader: row.user }}
                                style={{ color: "#dc2626", fontWeight: 600, textDecoration: "none" }}
                              >
                                {uuidStr}
                              </Link>
                            ) : (
                              uuidStr
                            )}
                          </td>
                          <td>{nameStr}</td>
                          <td>{emailStr}</td>
                          <td>{phoneStr}</td>
                          <td>
                            <strong title={row.pair?.margin_asset ? `Margin: ${row.pair.margin_asset}` : ""}>
                              {p}
                            </strong>
                          </td>
                          <td>
                            <span className={`openorders-side openorders-side--${(row.side || "").toLowerCase()}`}>
                              {row.side ?? "—"}
                            </span>
                          </td>
                          <td>{formatNum(qty)}</td>
                          <td>{formatNum(row.price)}</td>
                          <td
                            title={
                              marginAsset ? `Fee in ${marginAsset}` : undefined
                            }
                          >
                            {formatNum(fee)}
                            {marginAsset ? (
                              <span style={{ opacity: 0.75, fontSize: "0.85em" }}>
                                {" "}
                                {marginAsset}
                              </span>
                            ) : null}
                          </td>
                          <td>{formatNum(pnlVal)}</td>
                          <td>{roleVal ?? "—"}</td>
                          <td
                            title={
                              updatedAt && row.createdAt && updatedAt !== row.createdAt
                                ? `Updated: ${formatDate(updatedAt)}`
                                : undefined
                            }
                          >
                            {formatDate(row.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ padding: "0.75rem 0", display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="openorders-empty">No futures trades match your filters.</p>
          )}
        </section>
      </div>
    </main>
  );
};

export default FuturesTradeHistoryPage;
